import React, { useState } from 'react';
import { Card, Row, Col } from '@/components/ui';
import { formatDollars } from '../../../utils/formatDollars';
import './ProjectSummaryTotals.css';

interface ProjectSummaryTotals {
  totalBudget: number;
  totalCostsUsed: number;
  totalCostsInAction: number;
  totalCosts: number;
  totalValue: number;
  totalGrossMargin: number;
  totalGrossMarginPercent: number;
}

interface ProjectSummaryTotalsCardProps {
  totals: ProjectSummaryTotals;
}

const ProjectSummaryTotalsCard: React.FC<ProjectSummaryTotalsCardProps> = ({ totals }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const wholeDollarOpts = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

  const formatTotalsCurrency = (amount: number): string =>
    formatDollars(Math.round(amount), wholeDollarOpts);

  const formatPercent = (value: number): string => {
    if (isNaN(value) || !isFinite(value)) return '0.00%';
    return `${value.toFixed(2)}%`;
  };

  // Determine GM% color
  const getGMPercentColor = (percent: number): string => {
    if (percent <= 0) return 'red';
    if (percent > 0 && percent <= 10) return '#ffc107'; // yellow
    if (percent > 10) return 'green';
    return 'inherit';
  };

  return (
    <Card className="tw-mb-3 tw-shadow-sm project-summary-totals-card">
      <Card.Body
        className="project-summary-totals-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <div className="tw-flex tw-justify-between tw-items-center">
          <h6 className="tw-mb-0">
            <i className="bi bi-graph-up tw-mr-2 tw-text-primary"></i>
            Summary Totals
          </h6>
          <div className="tw-flex tw-items-center">
            <span className="tw-text-content-muted tw-mr-2 tw-text-sm">
              {isExpanded ? 'Click to collapse' : 'Click to expand'}
            </span>
            <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} tw-text-primary`}></i>
          </div>
        </div>
      </Card.Body>

      {isExpanded && (
        <Card.Body className="tw-border-t tw-border-line tw-bg-surface-alt collapse-transition">
          <Row>
            {/* Total Budget */}
            <Col lg={3} md={6}>
              <Card className="tw-h-full tw-border-0 tw-bg-primary/10 summary-metric">
                <Card.Body className="tw-text-center">
                  <i className="bi bi-cash-stack metric-icon tw-text-primary"></i>
                  <h6 className="tw-mb-2 tw-text-primary">Total Budget</h6>
                  <h4 className="metric-value tw-text-primary">
                    {formatTotalsCurrency(totals.totalBudget)}
                  </h4>
                </Card.Body>
              </Card>
            </Col>

            {/* Total Costs */}
            <Col lg={3} md={6}>
              <Card className="tw-h-full tw-border-0 tw-bg-danger/10 summary-metric">
                <Card.Body className="tw-text-center">
                  <i className="bi bi-tags metric-icon tw-text-danger"></i>
                  <h6 className="tw-mb-2 tw-text-danger">Total Costs</h6>
                  <h4 className="metric-value tw-text-danger">
                    {formatTotalsCurrency(totals.totalCosts)}
                  </h4>
                  <div className="metric-description tw-text-sm tw-text-content-muted">
                    Used: {formatTotalsCurrency(totals.totalCostsUsed)} | In Action: {formatTotalsCurrency(totals.totalCostsInAction)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Total Value */}
            <Col lg={3} md={6}>
              <Card className="tw-h-full tw-border-0 tw-bg-success/10 summary-metric">
                <Card.Body className="tw-text-center">
                  <i className="bi bi-cash-coin metric-icon tw-text-success"></i>
                  <h6 className="tw-mb-2 tw-text-success">Total Value</h6>
                  <h4 className="metric-value tw-text-success">
                    {formatTotalsCurrency(totals.totalValue)}
                  </h4>
                </Card.Body>
              </Card>
            </Col>

            {/* Total Gross Margin */}
            <Col lg={3} md={6}>
              <Card className={`tw-h-full tw-border-0 summary-metric ${totals.totalGrossMargin >= 0 ? 'net-difference-positive' : 'net-difference-negative'}`}>
                <Card.Body className="tw-text-center">
                  <i className={`bi ${totals.totalGrossMargin >= 0 ? 'bi-graph-up-arrow' : 'bi-graph-down-arrow'} metric-icon ${totals.totalGrossMargin >= 0 ? 'tw-text-success' : 'tw-text-danger'}`}></i>
                  <h6 className="tw-mb-2">Total Gross Margin</h6>
                  <h4 className={`metric-value ${totals.totalGrossMargin >= 0 ? 'tw-text-success' : 'tw-text-danger'}`}>
                    {formatTotalsCurrency(totals.totalGrossMargin)}
                  </h4>
                  <div className="metric-description" style={{ color: getGMPercentColor(totals.totalGrossMarginPercent) }}>
                    <strong>{formatPercent(totals.totalGrossMarginPercent)}</strong>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="tw-mt-3 tw-text-center">
            <small className="tw-text-content-muted">
              <i className="bi bi-info-circle tw-mr-1"></i>
              Totals are calculated from all projects in the current report view.
            </small>
          </div>
        </Card.Body>
      )}
    </Card>
  );
};

export default ProjectSummaryTotalsCard;

